import React, { useState } from "react";
import Styles from "./RAPlaceBid.module.css";
import { Modal } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useSelector } from "react-redux";

import TextInput from "../../../commons/Inputs/TextInput/TextInput";
import {
  createBid,
  updateBid,
} from "../../../Services/ReverseAuction/AlterApi";
import {
  formatDateTimeView,
  formatDateTimeViewRfq,
  formatDateType,
} from "../../../commons/FormatDate";
import Countdown from "../../../commons/CountDownTimer/Countdown";
import { ErrorMessage } from "../../../commons/Toast/ErrorMessage";
import ConfirmationModal from "../../../commons/ConfirmationModal/ConfirmationModal";
import Fileupload from "../../../commons/Inputs/FileUpload/Fileupload";
import { decryptData } from "../../../commons/Decode";
import { useLocation } from "react-router-dom";

const RAPlaceBid = ({
  open,
  handleClose,
  tenderData,
  requisitionData,
  bidStage,
  handleUpdate,
  status,
  allRequisitions,
  checkType,
}) => {
  const showSellerMinQty = true;
  const loacation = useLocation();

  const bidsPlaced = checkType?.filter((ele) => ele?.selfBid?.id) ?? [];
  const firstBid = bidsPlaced?.[0]?.selfBid;

  const isBgLocked =
    !!requisitionData?.selfBid?.documentId || !!firstBid?.documentId;
  const isEmdLocked = !!firstBid && !firstBid?.documentId;
  // Another requisition has BG uploaded — BG becomes mandatory here too
  const isBgMandatory =
    !isBgLocked &&
    !isEmdLocked &&
    (allRequisitions ?? [])
      .filter((r) => r.id !== requisitionData?.id)
      .some((r) => !!r?.selfBid?.documentId);
  // Another requisition chose EMD — EMD becomes mandatory here too
  const isEmdMandatory =
    !isBgLocked &&
    !isEmdLocked &&
    !isBgMandatory &&
    (allRequisitions ?? [])
      .filter((r) => r.id !== requisitionData?.id)
      .some((r) => !!r?.selfBid && !r?.selfBid?.documentId);

  const [securityMode, setSecurityMode] = useState(
    isBgLocked || isBgMandatory || requisitionData?.selfBid?.bankGuaranteeAmount
      ? "bg"
      : "emd",
  );
  const [bankGuaranteeAmount, setBankGuaranteeAmount] = useState(
    requisitionData?.selfBid?.bankGuaranteeAmount || "",
  );

  const role = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole,
  ).toLowerCase();
  const userId = useSelector(
    (state) => state?.persistedReducer?.user?.user?.userId,
  );
  const parentEntityCompanyId = useSelector(
    (state) => state?.persistedReducer?.user?.parentEntityCompanyId,
  );

  const showLow =
    new URLSearchParams(loacation.search).get("type") === "initiate" && role === "consumer";

  const [bidPrice, setBidPrice] = useState(
    requisitionData?.selfBid?.bidPrice || "",
  );
  const [bidQuantity, setBidQuantity] = useState(
    requisitionData?.selfBid?.bidQuantityMw || "",
  );
  const [minimumQuantity, setMinimumQuantity] = useState(
    requisitionData?.selfBid?.minBidQuantityMw ?? "",
  );
  const [docId, setDocId] = useState(
    requisitionData?.selfBid?.documentId || null,
  );
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    data: "",
    note: "",
  });
  const [securityModeConfirm, setSecurityModeConfirm] = useState({
    open: false,
    target: null,
  });
  const [bgDocConfirm, setBgDocConfirm] = useState({
    open: false,
    pendingVal: null,
  });
  const [bgUploadKey, setBgUploadKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const isUpdate = !!requisitionData?.selfBid;
  const isAuction = bidStage === "AUCTION";

  const handleSecurityModeClick = (target) => {
    if (target === securityMode) return;
    if (isBgLocked || isEmdLocked || isBgMandatory || isEmdMandatory) return;
    setSecurityModeConfirm({ open: true, target });
  };

  const confirmSecurityModeSwitch = () => {
    setSecurityMode(securityModeConfirm.target);
    setSecurityModeConfirm({ open: false, target: null });
  };

  const handleBidPrice = (value) => {
    setBidPrice(value);
  };

  const handleBidQuantity = (value) => {
    if (value < 0) {
      return ErrorMessage("Bid Quantity cannot be negative");
    }
    setBidQuantity(value);
  };

  const handleMinimumQuantity = (value) => {
    setMinimumQuantity(value);
  };

  const handleSubmit = async () => {
    if (isLoading) return;

    // --- AUCTION stage validations ---
    if (isAuction) {
      if (Number(bidPrice) > Number(requisitionData?.selfBid?.bidPrice)) {
        return ErrorMessage(
          `Bid Price cannot exceed current Bid Price (${requisitionData?.selfBid?.bidPrice} ₹/kWh)`,
        );
      }
      if (
        Number(bidQuantity) < Number(requisitionData?.selfBid?.bidQuantityMw)
      ) {
        return ErrorMessage(
          `Bid Quantity cannot be less than current Bid Quantity (${requisitionData?.selfBid?.bidQuantityMw} MW)`,
        );
      }
      if (Number(bidQuantity) > Number(requisitionData?.requiredQty)) {
        return ErrorMessage(
          `Bid Quantity cannot exceed Required Quantity (${requisitionData?.requiredQty} MW)`,
        );
      }
    }

    // --- IPO stage validations ---
    if (!isAuction) {
      if (Number(bidQuantity) < Number(requisitionData?.minimumQty)) {
        return ErrorMessage(
          `Bid Quantity cannot be less than Minimum Quantity (${requisitionData?.minimumQty} MW)`,
        );
      }
      if (Number(bidQuantity) > Number(requisitionData?.requiredQty)) {
        return ErrorMessage(
          `Bid Quantity cannot exceed Required Quantity (${requisitionData?.requiredQty} MW)`,
        );
      }
    }

    // --- Seller Min. Quantity validations ---
    if (showSellerMinQty) {
      if (
        requisitionData?.minimumQty &&
        Number(minimumQuantity) < Number(requisitionData?.minimumQty)
      ) {
        return ErrorMessage(
          `Seller Min. Quantity cannot be less than Buyer Min. Quantity (${requisitionData?.minimumQty} MW)`,
        );
      }
      if (Number(minimumQuantity) > Number(bidQuantity)) {
        return ErrorMessage(
          `Seller Min. Quantity cannot exceed Bid Quantity (${bidQuantity} MW)`,
        );
      }
    }

    // --- Security deposit validations ---
    if (securityMode === "bg" && !bankGuaranteeAmount) {
      return ErrorMessage("Please enter the Bank Guarantee amount.");
    }
    if (securityMode === "bg" && !docId) {
      return ErrorMessage("Please upload the Bank Guarantee document.");
    }

    try {
      setIsLoading(true);
      const tenderId = tenderData?.data?.id || tenderData?.id;
      let res;

      const securityPayload =
        securityMode === "bg" ? { bankGuaranteeAmount, documentId: docId } : {};

      if (isUpdate) {
        res = await updateBid({
          id: requisitionData.selfBid.id,
          tenderId,
          bidPrice,
          bidQuantityMw: bidQuantity,
          minBidQuantityMw: showSellerMinQty ? minimumQuantity : 0,
          requisitionId: requisitionData?.id,
          created_by: userId,
          ...securityPayload,
        });
      } else {
        res = await createBid({
          tenderId,
          requisitionId: requisitionData?.id,
          initiator: role,
          bidStage,
          bidPrice,
          bidQuantityMw: bidQuantity,
          minBidQuantityMw: showSellerMinQty ? minimumQuantity : 0,
          created_by: userId,
          ...securityPayload,
        });
      }

      const resData = await decryptData(res?.data);
      if (res?.data?.code === 200 || resData?.code === 200) {
        handleUpdate();
        handleClose();
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
      setConfirmModal({ open: false, data: "", note: "" });
    }
  };

  return (
    <>
      {confirmModal.open && (
        <ConfirmationModal
          open={confirmModal.open}
          handleClose={() =>
            setConfirmModal({ open: false, data: "", note: "" })
          }
          data={confirmModal.data}
          note={confirmModal.note}
          handleSubmit={handleSubmit}
        />
      )}
      {bgDocConfirm.open && (
        <ConfirmationModal
          open={bgDocConfirm.open}
          handleClose={() => {
            setBgDocConfirm({ open: false, pendingVal: null });
            setBgUploadKey((k) => k + 1);
          }}
          header="BG Document Upload"
          data="Please ensure that the correct Bank Guarantee (BG) is uploaded and that the original BG is submitted as required. Bids accompanied by an incorrect or invalid Bank Guarantee will be rejected."
          handleSubmit={() => {
            setDocId(bgDocConfirm.pendingVal?.id);
            setBgDocConfirm({ open: false, pendingVal: null });
          }}
        />
      )}
      {securityModeConfirm.open && (
        <ConfirmationModal
          open={securityModeConfirm.open}
          handleClose={() =>
            setSecurityModeConfirm({ open: false, target: null })
          }
          header={
            securityModeConfirm.target === "emd"
              ? "Switch to EMD"
              : "Switch to Bank Guarantee (BG)"
          }
          data={
            securityModeConfirm.target === "emd"
              ? "Amount will be deducted from the escrow based on the System T&C."
              : "You need to upload the BG document and specify the BG Amount to proceed."
          }
          handleSubmit={confirmSecurityModeSwitch}
        />
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="place-bid-modal"
      >
        <div className={`popupModal ${Styles.createEditModalCont}`}>
          {/* Header */}
          <div
            className={`display_flex_row modalHeading ${Styles.headingText}`}
          >
            <div className={`headingText ${Styles.finalHead}`}>
              {isUpdate ? "Update" : "Place"} Bid(s)
            </div>
            <Close
              style={{ fontSize: "24px", color: "#777777", cursor: "pointer" }}
              onClick={handleClose}
            />
          </div>

          <div className={Styles.containerMainElem}>
            {/* Bidding Duration + Countdown */}
            <div className={Styles.detailsMainElements}>
              <div className="display_flex_row">
                <div
                  className={`display_flex_column_gap_8 ${Styles.reqMainElem}`}
                >
                  <div>
                    {status?.includes("ra_ongoing") ? "Auction" : "Bidding"}{" "}
                    Duration
                  </div>
                  <div className={Styles.tenderDateTime}>
                    <div className={Styles.tenderDate}>
                      <span>
                        {
                          formatDateTimeViewRfq(
                            tenderData?.bidStartDateTime,
                          )?.split("-")?.[0]
                        }
                      </span>
                      <span>
                        {
                          formatDateTimeViewRfq(
                            tenderData?.bidStartDateTime,
                          )?.split("-")?.[1]
                        }
                      </span>
                    </div>
                    <div>to</div>
                    <div className={Styles.tenderDate}>
                      <span>
                        {
                          formatDateTimeViewRfq(
                            tenderData?.bidCloseDateTime,
                          )?.split("-")?.[0]
                        }
                      </span>
                      <span>
                        {
                          formatDateTimeViewRfq(
                            tenderData?.bidCloseDateTime,
                          )?.split("-")?.[1]
                        }
                      </span>
                    </div>
                  </div>
                  {/* <div>
                    {formatDateTimeView(
                      status?.includes("ra_ongoing")
                        ? tenderData?.auctionStartDateTime
                        : tenderData?.bidStartDateTime,
                    )}{" "}
                    to{" "}
                    {formatDateTimeView(
                      status?.includes("ra_ongoing")
                        ? tenderData?.auctionCloseDateTime
                        : tenderData?.bidCloseDateTime,
                    )}
                  </div> */}
                </div>
                <Countdown status={status} tenderData={tenderData} type="bid" />
              </div>

              {/* Requisition Details */}
              <div className={`display_flex_row ${Styles.reqMainContainer}`}>
                <div
                  className={`display_flex_column_gap_8 ${Styles.reqMainElem}`}
                >
                  <div>Start and End Date</div>
                  <div>
                    {formatDateType(requisitionData?.dateStartAt)} to{" "}
                    {formatDateType(requisitionData?.dateEndAt)}
                  </div>
                </div>
                <div
                  className={`display_flex_column_gap_8 ${Styles.reqMainElem}`}
                >
                  <div>Start and End Time</div>
                  <div>
                    {requisitionData?.slotStartAt} -{" "}
                    {requisitionData?.slotEndAt === "23:59"
                      ? "24:00"
                      : requisitionData?.slotEndAt}
                  </div>
                </div>
                <div
                  className={`display_flex_column_gap_8 ${Styles.reqMainElem}`}
                >
                  <div>Req Qty (MW)</div>
                  <div>{requisitionData?.requiredQty} (MW)</div>
                </div>
                <div
                  className={`display_flex_column_gap_8 ${Styles.reqMainElem}`}
                >
                  <div>Min Qty (MW)</div>
                  <div>{requisitionData?.minimumQty} (MW)</div>
                </div>
                <div
                  className={`display_flex_column_gap_8 ${Styles.reqMainElem}`}
                >
                  <div>Self Bid Qty (MW)</div>
                  <div className={Styles.active_text}>
                    {requisitionData?.selfBid?.bidQuantityMw ?? "-"}
                  </div>
                </div>
                <div
                  className={`display_flex_column_gap_8 ${Styles.reqMainElem}`}
                >
                  <div>Self Bid Price (₹/kWh)</div>
                  <div className={Styles.active_text}>
                    {requisitionData?.selfBid?.bidPrice ?? "-"}
                  </div>
                </div>
                {/* <div
                  className={`display_flex_column_gap_8 ${Styles.reqMainElem}`}
                >
                  <div>{showLow ? "Lowest" : "Highest"} Bid Price (₹/kWh)</div>
                  <div className={Styles.active_text}>
                    {showLow ? requisitionData?.lowestBidPrice : requisitionData?.highestBidPrice ?? "-"}
                  </div>
                </div> */}
              </div>
            </div>

            {/* Input Fields — 2-column layout */}
            <div className={Styles.sectionsGrid}>
              {/* Left: Bid Details */}
              <div className={Styles.sectionCard}>
                <p className={Styles.sectionLabel}>Bid details</p>
                <div className={Styles.sectionFields}>
                  <TextInput
                    allowDecimal={true}
                    heading="Bid Quantity (MW)*"
                    value={bidQuantity}
                    onChange={handleBidQuantity}
                    example="Eg. 20.32"
                    type="number"
                  />
                  {showSellerMinQty && (
                    <TextInput
                      allowDecimal={true}
                      heading="Seller Min. Quantity (MW)*"
                      value={minimumQuantity}
                      onChange={handleMinimumQuantity}
                      example="Eg. 20.00"
                      type="number"
                      disabled={isUpdate}
                    />
                  )}
                  <TextInput
                    allowDecimal={true}
                    heading="Bid Price (₹/kWh)*"
                    value={bidPrice}
                    onChange={handleBidPrice}
                    example="Eg. 10"
                    type="number"
                  />
                </div>
              </div>

              {/* Right: Security Deposit */}
              <div
                className={Styles.sectionCard}
                style={isAuction ? { opacity: 0.7 } : {}}
              >
                <div className={Styles.sectionLabelRow}>
                  <p className={Styles.sectionLabel}>Security deposit</p>
                  {isBgMandatory && (
                    <span className={Styles.mandatoryBadge}>BG Required</span>
                  )}
                  {isEmdMandatory && (
                    <span className={Styles.mandatoryBadge}>EMD Required</span>
                  )}
                  {isBgLocked && (
                    <span className={Styles.lockedBadge}>🔒 Locked</span>
                  )}
                  {isEmdLocked && (
                    <span className={Styles.lockedBadge}>🔒 Locked</span>
                  )}
                </div>
                <div
                  className={Styles.toggleBar}
                  style={
                    isAuction ||
                    isBgLocked ||
                    isEmdLocked ||
                    isBgMandatory ||
                    isEmdMandatory
                      ? { pointerEvents: "none", opacity: 0.55 }
                      : {}
                  }
                >
                  <button
                    className={`${Styles.toggleBtn} ${securityMode === "emd" ? Styles.toggleBtnActive : ""}`}
                    onClick={() => handleSecurityModeClick("emd")}
                  >
                    EMD
                  </button>
                  <button
                    className={`${Styles.toggleBtn} ${securityMode === "bg" ? Styles.toggleBtnActive : ""}`}
                    onClick={() => handleSecurityModeClick("bg")}
                  >
                    BG
                  </button>
                </div>

                {isBgMandatory && (
                  <p className={Styles.stateCaption}>
                    Another requisition has BG uploaded — BG is mandatory for
                    all requisitions.
                  </p>
                )}
                {isEmdMandatory && (
                  <p className={Styles.stateCaption}>
                    Another requisition has EMD selected — EMD is mandatory for
                    all requisitions.
                  </p>
                )}
                {isBgLocked && (
                  <p className={Styles.stateCaption}>
                    BG has been submitted and cannot be modified.
                  </p>
                )}
                {isEmdLocked && (
                  <p className={Styles.stateCaption}>
                    EMD has been selected and cannot be changed.
                  </p>
                )}

                {securityMode === "emd" && (
                  <p className={Styles.stateCaption}>
                    Amount will be deducted from the escrow based on System T&C.
                  </p>
                )}

                {securityMode === "bg" && (
                  <div className={Styles.sectionFields}>
                    <TextInput
                      heading="Bank Guarantee Amount (₹)*"
                      value={bankGuaranteeAmount}
                      onChange={(val) => setBankGuaranteeAmount(val)}
                      example="Eg. 500000"
                      type="number"
                      disabled={isAuction || isBgLocked}
                    />
                    <Fileupload
                      key={bgUploadKey}
                      accept=".pdf"
                      id="bg-document-upload"
                      value={docId}
                      onChange={(val) => {
                        if (isBgLocked) return;
                        if (val?.id) {
                          setBgDocConfirm({ open: true, pendingVal: val });
                        } else {
                          setDocId(undefined);
                        }
                      }}
                      height="35"
                      heading="BG Document"
                      example="Upload BG (.pdf)"
                      fileType="pdf"
                      isInPlaceBid={true}
                      disabled={isAuction || isBgLocked}
                      viewMode={isAuction || isBgLocked}
                      previewTitle="Bank Guarantee Document Preview"
                      documentType="BANK_GUARANTEE"
                      parentEntityCompanyId={parentEntityCompanyId}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className={`display_flex_row_gap_8 ${Styles.buttonContianer}`}>
            <div className="activeButton_type_3" onClick={handleClose}>
              Cancel
            </div>
            <div
              className="activeButton"
              onClick={() =>
                setConfirmModal({
                  open: true,
                  data: `Do you want to ${isUpdate ? "Update" : "Place"} the Bid?`,
                  note: isUpdate
                    ? `Current Bid: ${bidQuantity}MW/₹${bidPrice}/kWh,\n Previous Bid: ${requisitionData.selfBid?.bidQuantityMw}MW/₹${requisitionData.selfBid?.bidPrice}/kWh`
                    : securityMode !== "bg" &&
                      "EMD Amount will be blocked as per Terms and Conditions, if applicable.",
                })
              }
            >
              {isLoading ? "Loading..." : isUpdate ? "Update" : "Submit"}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default RAPlaceBid;
